import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';
import Compressor from 'compressorjs';
import { v4 as uuidv4 } from 'uuid';
import type { Media, MediaSortOrder, MediaUploadResponse } from '../@types/Media';
import type { BaseAction } from '../@types/actions';
import * as http from '../Http';
import {
  buildAuthorizedDeleteRequest,
  buildAuthorizedFormPostRequest,
  buildAuthorizedGetRequest,
  buildAuthorizedPutRequest,
} from '../Http/requestTemplates';
import { get as getSettings } from '../helpers/settings';
import type { RootState } from '../reducers';
import type { MediaListItem } from '../routes/assignments/Components/media-selections';
import requestCache from '../utils/request-cache';

export const REQ_MEDIA_LIST = 'REQ/MEDIA_LIST';
export const RCV_MEDIA_LIST = 'RCV/MEDIA_LIST';
type REQ_MEDIA_LIST_TYPE = BaseAction<typeof REQ_MEDIA_LIST>;
type RCV_MEDIA_LIST_TYPE = BaseAction<typeof RCV_MEDIA_LIST> & {
  response: Media[];
  advertisement: { id: string };
};

export const REQ_UPLOAD_MEDIA = 'REQ/UPLOAD_MEDIA';
export const RCV_UPLOAD_MEDIA = 'RCV/UPLOAD_MEDIA';
type REQ_UPLOAD_MEDIA_TYPE = BaseAction<typeof REQ_UPLOAD_MEDIA>;
type RCV_UPLOAD_MEDIA_TYPE = BaseAction<typeof RCV_UPLOAD_MEDIA> & {
  response: MediaUploadResponse;
  sortOrder: number;
  file: File;
};

export const REQ_REMOVE_MEDIA = 'REQ/REMOVE_MEDIA';
export const RCV_REMOVE_MEDIA = 'RCV/REMOVE_MEDIA';
type REQ_REMOVE_MEDIA_TYPE = BaseAction<typeof REQ_REMOVE_MEDIA>;
type RCV_REMOVE_MEDIA_TYPE = BaseAction<typeof RCV_REMOVE_MEDIA> & {
  id: string;
};

export const REQ_SORT_MEDIA = 'REQ/SORT_MEDIA';
export const RCV_SORT_MEDIA = 'RCV/SORT_MEDIA';
type REQ_SORT_MEDIA_TYPE = BaseAction<typeof REQ_SORT_MEDIA>;
type RCV_SORT_MEDIA_TYPE = BaseAction<typeof RCV_SORT_MEDIA>;

export const REQ_STAGE_SORT = 'REQ/STAGE_SORT';
type REQ_STAGE_SORT_TYPE = BaseAction<typeof REQ_STAGE_SORT> & {
  media: MediaSortOrder[];
};

export const REQ_STAGE_REMOVED = 'REQ/STAGE_REMOVED';
type REQ_STAGE_REMOVED_TYPE = BaseAction<typeof REQ_STAGE_REMOVED> & {
  media: Media | MediaListItem;
};

export const REQ_CLEAR_UPLOADS = 'REQ/CLEAR_MEDIA_UPLOADS';
type REQ_CLEAR_UPLOADS_TYPE = BaseAction<typeof REQ_CLEAR_UPLOADS>;

export const REQUEST_FAILED = 'MEDIA/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action:
    | typeof REQ_MEDIA_LIST
    | typeof REQ_UPLOAD_MEDIA
    | typeof REQ_REMOVE_MEDIA
    | typeof REQ_SORT_MEDIA;
};

export type MEDIA_ACTION =
  | REQ_MEDIA_LIST_TYPE
  | RCV_MEDIA_LIST_TYPE
  | REQ_UPLOAD_MEDIA_TYPE
  | RCV_UPLOAD_MEDIA_TYPE
  | REQ_REMOVE_MEDIA_TYPE
  | RCV_REMOVE_MEDIA_TYPE
  | REQ_SORT_MEDIA_TYPE
  | RCV_SORT_MEDIA_TYPE
  | REQ_STAGE_SORT_TYPE
  | REQ_STAGE_REMOVED_TYPE
  | REQ_CLEAR_UPLOADS_TYPE
  | REQUEST_FAILED_TYPE;

type Dispatch = ReduxDispatch<MEDIA_ACTION>;

const queue: (() => void)[] = [];
let pendingQueue = false;

const executeNextQueue = () => {
  if (queue.length) {
    const m = queue.pop();
    if (m) {
      m();
    }
  } else if (pendingQueue) {
    pendingQueue = false;
  }
};

export const all =
  (advertisement: { id: string }) => async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token) return;

    try {
      dispatch({ type: REQ_MEDIA_LIST });
      const response = await http.httpJson<Media[]>(
        `${getSettings('mediaServiceUri')}/wayke-id/${advertisement.id}`,
        buildAuthorizedGetRequest(auth.token),
      );
      dispatch({
        type: RCV_MEDIA_LIST,
        response,
        advertisement,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_MEDIA_LIST });
    }
  };

const offileUploadResponseHandle = (file: File, sortOrder: number) => (dispatch: Dispatch) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = async () => {
    const base64 = reader.result as string;

    dispatch({
      type: RCV_UPLOAD_MEDIA,
      response: {
        fileId: uuidv4(),
        mediaId: uuidv4(),
        url: base64,
      },
      sortOrder,
      file,
    });
  };
};

const offlineUpload = (file: File, sortOrder: number) => (dispatch: Dispatch) => {
  dispatch({ type: REQ_UPLOAD_MEDIA });
  new Compressor(file, {
    quality: 0.4,
    convertTypes: ['image/png', 'image/jpg', 'image/jpeg'],
    convertSize: 2000000,
    success(compressedFile) {
      offileUploadResponseHandle(compressedFile as File, sortOrder)(dispatch);
    },
    error() {
      offileUploadResponseHandle(file, sortOrder)(dispatch);
    },
  });
};

export const upload =
  (file: File, sortOrder: number, purpose = 'Vehicle', allowOfflineUpload = false) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token) {
      if (allowOfflineUpload) {
        offlineUpload(file, sortOrder)(dispatch);
      }
      return;
    }

    const data = new FormData();

    data.append('file', file);
    data.append('Metadata.Purpose', purpose);
    data.append('Metadata.SortOrder', String(sortOrder));

    try {
      dispatch({ type: REQ_UPLOAD_MEDIA });
      const response = await http.httpJson<MediaUploadResponse>(
        `${getSettings('mediaServiceUri')}/image`,
        buildAuthorizedFormPostRequest(auth.token, { content: data }),
      );

      dispatch({
        type: RCV_UPLOAD_MEDIA,
        response,
        sortOrder,
        file,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_UPLOAD_MEDIA });
    }
  };

export const remove =
  (media: Media | MediaListItem, silent = false) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const key = `${REQ_REMOVE_MEDIA}/${media.id}`;
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(key)) return;

    requestCache.isPending(key, true);

    try {
      dispatch({ type: REQ_REMOVE_MEDIA });
      await http.json<null>(
        `${getSettings('mediaServiceUri')}/${media.id}`,
        buildAuthorizedDeleteRequest(auth.token),
      );
      dispatch({
        type: RCV_REMOVE_MEDIA,
        id: media.id,
      });
    } catch (_e) {
      if (silent) {
        dispatch({ type: RCV_REMOVE_MEDIA, id: media.id });
      } else {
        dispatch({ type: REQUEST_FAILED, action: REQ_REMOVE_MEDIA });
      }
    } finally {
      executeNextQueue();
      requestCache.isPending(key, false);
    }
  };

export const sort =
  (list: MediaSortOrder[]) => async (dispatch: Dispatch, getState: () => RootState) => {
    const { auth } = getState();
    if (!auth.token || requestCache.isPending(REQ_SORT_MEDIA)) return false;

    requestCache.isPending(REQ_SORT_MEDIA, true);

    try {
      dispatch({ type: REQ_SORT_MEDIA });
      await http.json<null>(
        `${getSettings('mediaServiceUri')}/sort-order`,
        buildAuthorizedPutRequest(auth.token, { content: { sortOrder: list } }),
      );
      dispatch({
        type: RCV_SORT_MEDIA,
      });
    } catch (_e) {
      dispatch({ type: REQUEST_FAILED, action: REQ_SORT_MEDIA });
    } finally {
      executeNextQueue();
      requestCache.isPending(REQ_SORT_MEDIA, false);
    }
  };

export const clearUploads = () => (dispatch: Dispatch) => dispatch({ type: REQ_CLEAR_UPLOADS });
export const stageRemove = (media: Media | MediaListItem) => (dispatch: Dispatch) =>
  dispatch({ type: REQ_STAGE_REMOVED, media });
export const stageSort = (media: MediaSortOrder[]) => (dispatch: Dispatch) =>
  dispatch({ type: REQ_STAGE_SORT, media });
